<template>
    <q-expansion-item
        :dense="$q.screen.gt.sm"
        :header-class="{ 'text-grey-8': !$q.dark.isActive }"
        :header-style="$q.dark.isActive ? `background-color: #0f0f0f !important;` : ''"
        :model-value="$url.searchParams.get('workItem') == workItem.id"
    >
        <template #header="{ expanded }">
            <q-separator 
                vertical
                style="width: 5px;position: absolute;left: 0;top: 0;bottom: 0;"
                :color="expanded ? 'secondary' : 'transparent'"
            />
            <q-item-section>
                <q-item-label class="text-bold">
                    {{ workItem.work_type?.name ?? '' }} <span :class="`text-grey-${$q.dark.isActive ? 4 : 6}`" style="vertical-align: middle;font-size: smaller;">(ID:{{ workItem.id }})</span>{{ 
                        $q.screen.lt.md ? '' : (' - ' + (workItem?.sub_customer?.contact?.org_name ?? workItem.customer?.contact?.org_name ?? 'Unknown')) 
                    }}
                </q-item-label>
                <q-item-label v-if="$q.screen.lt.md">
                    {{ workItem.sub_customer?.contact?.org_name ?? workItem.customer?.contact?.org_name ?? 'Unknown' }}
                </q-item-label>
            </q-item-section>
            <q-item-section v-if="$q.screen.gt.sm" class="q-gutter-sm">
                <q-item-label class="text-bold">
                    <q-chip
                        square
                        size="0.65rem"
                        v-for="row in workItem.identifiers"
                        :icon="row.type.icon"
                        color="accent"
                        :text-color="processDarkRadial('accent')"
                        :class="{ 'q-mr-xs': 1 }"
                    >
                        {{ truncateString(row.value) }}
                        <q-tooltip
                            :class="['bg-accent', 'text-white']"
                        >
                            <div v-if="row.type.title">{{row.type.title}}</div>
                            <div v-if="row.type.description">{{row.type.description}}</div>
                        </q-tooltip>
                    </q-chip>
                    <template v-for="row in workItem.values">
                        <q-chip
                            square
                            size="0.65rem"
                            v-if="workItem.bracket_types.find(v => {
                                return v.id == row.type.id;
                            }) !== undefined"
                            :icon="row.type.icon"
                            :color="$q.dark.isActive ? row.type.color_dark : row.type.color"
                            :style="`color: ${calculateDarkOrLight($q.dark.isActive ? row.type.color_dark : row.type.color, 'white', 'black')}`"
                            :class="{
                                'q-mr-xs': 1,
                            }"
                        >
                            {{ row.text }}
    
                            <q-tooltip
                                :class="['bg-' + ($q.dark.isActive ? row.type.color_dark : row.type.color), 'text-white']"
                            >
                                <div v-if="row.type.title">{{row.type.title}}</div>
                                <div v-if="row.type.description">{{row.type.description}}</div>
                            </q-tooltip>
                        </q-chip>
                    </template>
                </q-item-label>
            </q-item-section>

            <q-item-section side>
                <q-icon 
                    name="mdi-note-alert"
                    v-if="workItem.notes ?? false"
                >
                    <q-tooltip>
                        {{ workItem.notes }}
                    </q-tooltip>
                </q-icon>
            </q-item-section>

            <q-item-section side :class="{'text-white':$q.dark.isActive}">
                <q-item-label class="flex justify-center" style="align-items: center;cursor: default;font-size: 0.875rem;flex-direction: row;height: 100%;">
                    <q-icon name="mdi-account-hard-hat" style="margin-right:3px;" size="0.875rem" /> {{ workItem.pay.length ?? 0 }}
                    <q-icon 
                        size="0.5rem"
                        class="q-pl-xs"
                        :style="`color: ${calculateGradientColor(workItem.total_labor_split, 0, 100)};`"
                        :name="`mdi-circle-slice-${Math.floor(8*((workItem.total_labor_split / 100) - 0.12) / 0.76) - 1}`"
                        v-if="workItem.total_labor_split"
                    />
                    <q-icon v-else name="mdi-exclamation-thick" color="red-12" />
                    <q-tooltip :class="{'bg-red-12 text-bold': !workItem.total_labor_split}">
                        {{ workItem.pay.length }} worker{{ workItem.pay.length != 1 ? 's' : '' }} with {{ workItem.total_labor_split }}% allocation.
                    </q-tooltip>
                </q-item-label>
                <q-item-label>&nbsp;</q-item-label>
            </q-item-section>
            <q-item-section v-if="$q.screen.gt.md" :style="!!workItem.billing_pay ? 'padding-right: 2.5px !important;' : ''" side>
                <q-item-label>
                    <q-badge :color="!$q.dark.isActive ? 'secondary' : 'white'" :outline="$q.dark.isActive">
                        {{ workItem.total_lumper_pay == null ? '$0.00' : toUSD(workItem.total_lumper_pay) }}
                        
                        <q-tooltip>
                            Total amount paid to workers
                        </q-tooltip>
                    </q-badge>
                </q-item-label>
            </q-item-section>
            <q-item-section :style="($q.screen.gt.md) ? 'padding-left: 2.5px !important;' : ''" side>
                <q-item-label v-if="$q.screen.lt.lg">
                    <q-badge :color="!$q.dark.isActive ? 'secondary' : 'white'" :outline="$q.dark.isActive">
                        {{ workItem.total_lumper_pay == null ? '$0.00' : toUSD(workItem.total_lumper_pay) }}
                        
                        <q-tooltip>
                            Total amount paid to workers
                        </q-tooltip>
                    </q-badge>
                </q-item-label>
                <q-item-label class="row" v-if="isAdmin() || isManager()">
                    <q-badge
                        :outline="$q.dark.isActive"
                        color="green-13"
                        :text-color="isDarkRadial('#00e676', 'white', 'black')"
                    >
                        {{ workItem.billing_pay == null ? '$0.00' : toUSD(workItem.billing_pay) }}
                        
                        <q-tooltip class="bg-positive text-white">
                            Total amount billed to the customers
                        </q-tooltip>
                    </q-badge>
                </q-item-label>
            </q-item-section>
        </template>
        
        <q-list dense separator>
            <q-item v-if="editable">
                <q-item-section>
                    <q-item-label class="text-bold">Actions</q-item-label>
                </q-item-section>
                <q-item-section side>
                    <q-btn 
                        no-caps
                        icon="mdi-delete" 
                        size="sm" 
                        color="red-12"
                        :label="$q.screen.gt.md ? 'Delete Work Item' : undefined"
                        @click="$emit('delete', workItem.id)"
                    />
                </q-item-section>
            </q-item>

            <q-expansion-item 
                dense
                expand-icon-class="hidden"
            >
                <template #header="{ expanded }">
                    <q-item-section style="padding-right: 5px;" side>
                        <q-item-label>
                            <q-icon size="xs" name="mdi-account" />
                        </q-item-label>
                    </q-item-section>
                    <q-item-section 
                        :class="{
                            'text-underline': 1,
                            'text-grey-7': !$q.dark.isActive,
                            'text-grey-2': $q.dark.isActive,
                        }"
                    >Customer</q-item-section>
                    <q-item-section side>
                        <q-item-label class="text-bold text-light-blue-13">
                            <q-icon size="1rem" style="transition: transform 0.15s ease;" name="mdi-chevron-down" :class="{'rotate-180':expanded}" />
                        </q-item-label>
                    </q-item-section>
                    <q-item-section class="text-light-blue-13 text-bold" style="padding-left: 2.5px !important;" side>
                        {{ workItem.sub_customer?.contact?.org_name ?? workItem.customer?.contact?.org_name ?? 'Unknown' }}
                    </q-item-section>
                </template>

                <ContactViewModel 
                    :striped="false" 
                    :contact="workItem.sub_customer?.contact ?? workItem.customer?.contact" 
                />
            </q-expansion-item>

            <q-expansion-item dense expand-icon-class="hidden">
                <template #header="{ expanded }">
                    <q-item-section style="padding-right: 5px;" side>
                        <q-item-label>
                            <q-icon size="xs" name="mdi-account-hard-hat" />
                        </q-item-label>
                    </q-item-section>
                    <q-item-section
                        :class="{
                            'text-underline': 1,
                            'text-grey-7': !$q.dark.isActive,
                            'text-grey-2': $q.dark.isActive,
                        }"
                    >
                        {{ workItem.pay.length }} Worker{{ workItem.pay.length != 1 ? 's' : '' }}
                    </q-item-section>
                    <q-item-section>
                        <q-item-label class="q-gutter-xs">
                            <Avatar
                                size="1.25rem"
                                :user="worker"
                                v-for="({worker}, i) in workItem.pay"
                            >
                                <q-tooltip>
                                    {{ worker.name }}
                                </q-tooltip>
                            </Avatar>
                        </q-item-label>
                    </q-item-section>
                    <q-item-section side>
                        <q-btn 
                            size="sm"
                            padding="3px"
                            @click.stop.prevent="workerSplit = true"
                            icon="mdi-percent"
                            color="primary"
                            v-if="editable"
                        >
                            <q-tooltip class="bg-primary text-white">
                                Split Worker Pay
                            </q-tooltip>
                        </q-btn>
                    </q-item-section>
                    <q-item-section side>
                        <q-item-label class="text-bold text-light-blue-13">
                            <q-icon size="1rem" style="transition: transform 0.15s ease;" name="mdi-chevron-down" :class="{'rotate-180':expanded}" />
                        </q-item-label>
                    </q-item-section>
                    <q-item-section
                        side
                        class="text-light-blue-13 text-bold"
                        :style="`padding-left: 2.5px !important;`"
                    >
                        <q-item-label>
                            Total: {{ workItem.total_labor_split }}%
                            <q-tooltip>
                                {{ workItem.total_labor_split }}% of the total labor cost allocated
                            </q-tooltip>
                        </q-item-label>
                    </q-item-section>

                    <q-item-section side style="padding-left: 10px;">
                        <q-icon 
                            size="0.5rem"
                            :style="`color: ${calculateGradientColor(workItem.total_labor_split, 0, 100)};`"
                            :name="`mdi-circle-slice-${Math.floor(8*((workItem.total_labor_split / 100) - 0.12) / 0.76) - 1}`"
                        />
                    </q-item-section>
                </template>
                
                <q-list dense :separator="$q.dark.isActive">
                    <q-item v-for="(lp, y) in workItem.pay" :class="{ 'bg-grey-1': y % 2 === 0 && !$q.dark.isActive }">
                        <q-item-section class="text-grey-7" side>{{ y + 1 }}</q-item-section>
                        <q-item-section class="q-pr-xs" side>
                            <Avatar
                                size="1.25rem"
                                :user="lp.worker" 
                            />
                        </q-item-section>
                        <q-item-section>
                            <q-item-label>
                                {{ lp.worker.name ?? '*No Record*' }} <span class="text-bold">({{ lp.split }}%)</span>
                            </q-item-label>
                        </q-item-section>
                        <q-item-section side>
                            <span class="text-underline cursor-pointer">
                                {{ lp.hours ?? 0 }} hr{{ lp.hours != 1 ? 's' : '' }}
                                <q-menu>
                                    <q-item dense>
                                        <q-item-section v-if="lp.hours > 0">
                                            <q-item-label>
                                                <q-icon
                                                    name="mdi-timer-play-outline"
                                                    class="q-mr-xs"
                                                />
                                                {{ format(lp.time_start) }}
                                            </q-item-label>
                                            <q-item-label>
                                                <q-icon
                                                    name="mdi-timer-stop-outline"
                                                    class="q-mr-xs"
                                                />
                                                {{ format(lp.time_end) }}
                                            </q-item-label>
                                        </q-item-section>
                                        <q-item-section v-else>
                                            <q-item-label>
                                                No time clock entered.
                                            </q-item-label>
                                        </q-item-section>
                                    </q-item>
                                </q-menu>
                            </span>
                        </q-item-section>
                        <q-item-section :class="{ 'text-positive': fixedFloat(lp.pay) > 0 }" side>{{ toUSD(lp.pay) }}</q-item-section>
                    </q-item>

                    <q-item v-if="!workItem.pay.length">
                        <q-item-section class="text-center">
                            <q-item-label class="text-grey-6">*No Workers Found!*</q-item-label>
                            <q-item-label
                                caption
                                @click="workerSplit = true"
                                class="text-secondary text-underline cursor-pointer"
                                v-if="editable"
                            >Add workers</q-item-label>
                        </q-item-section>
                    </q-item>
                </q-list>

                <!-- Show lumpers -->
            </q-expansion-item>

            <!-- Work Type -->
            <q-item>
                <q-item-section style="padding-right: 5px;" side>
                    <q-item-label>
                        <q-icon size="xs" name="mdi-hammer-wrench" />
                    </q-item-label>
                </q-item-section>
                <q-item-section 
                    :class="{
                        'text-grey-7': !$q.dark.isActive,
                        'text-grey-2': $q.dark.isActive,
                    }"
                >Work Type</q-item-section>
                <q-item-section 
                    style="padding-right: 3.5px;" 
                    :class="['text-bold', 'text-' + ($q.dark.isActive ? 'white' : 'black')]"
                    side
                >{{ workItem.work_type?.name ?? '*Unknown*' }}</q-item-section>
            </q-item>
            
            <!-- WorkItem Identifiers -->
            <q-item v-for="identifier in workItem.identifiers">
                <q-item-section style="padding-right: 5px;" side>
                    <q-item-label>
                        <q-icon size="xs" color="accent" :name="identifier.type.icon" />
                    </q-item-label>
                </q-item-section>
                
                <q-item-section 
                    :class="{
                        'text-grey-7': !$q.dark.isActive,
                        'text-grey-2': $q.dark.isActive,
                    }"
                >
                    {{ identifier.type.title }}
                </q-item-section>

                <q-item-section side :class="{
                    'text-bold':1,
                    'text-black':!$q.dark.isActive,
                    'text-grey-2': $q.dark.isActive,
                }">
                    {{ identifier.value }}
                </q-item-section>

                <q-item-section side style="padding-left: 10px;" :class="`text-accent q-pa-none q-ma-none`">
                    <q-icon size="0.5rem" name="mdi-circle" />
                </q-item-section>

                <q-tooltip
                    :class="['bg-accent', 'text-white']"
                    anchor="bottom right" self="center right"
                >
                    <div v-if="identifier.type.title">{{identifier.type.title}}</div>
                    <div v-if="identifier.type.description">{{identifier.type.description}}</div>
                </q-tooltip>
            </q-item>

            <!-- WorkItem values -->
            <q-item
                v-for="row in workItem.values"
                :class="{hidden: (workItem.bracket_types.find(v => v.id == row.type.id) === undefined) }"
            >
                <q-item-section style="padding-right: 5px;" side>
                    <q-item-label>
                        <q-icon 
                            size="xs"
                            :name="row.type.icon"
                            :color="$q.dark.isActive ? row.type.color_dark : row.type.color"
                        />
                    </q-item-label>
                </q-item-section>

                <q-item-section
                    :class="{
                        'text-grey-7': !$q.dark.isActive,
                        'text-grey-2': $q.dark.isActive,
                    }"
                >
                    {{ row.type.title }}
                </q-item-section>

                <q-item-section side :class="{
                    'text-bold':1,
                    'text-black':!$q.dark.isActive,
                    'text-grey-2': $q.dark.isActive,
                }">
                    {{ row.text }}
                </q-item-section>

                <q-item-section side style="padding-left: 10px;" :class="`text-${($q.dark.isActive ? row.type.color_dark : row.type.color)} q-pa-none q-ma-none`">
                    <q-icon size="0.5rem" name="mdi-circle" />
                </q-item-section>

                <q-tooltip
                    :class="['bg-' + ($q.dark.isActive ? row.type.color_dark : row.type.color)]"
                    :style="`color: ${calculateDarkOrLight($q.dark.isActive ? row.type.color_dark : row.type.color, 'white', 'black')}`"
                    anchor="bottom right" self="center right"
                >
                    <div v-if="row.type.title">{{row.type.title}}</div>
                    <div v-if="row.type.description">{{row.type.description}}</div>
                </q-tooltip>
            </q-item>
            
            <q-item>
                <q-item-section style="padding-right: 5px;" side>
                    <q-item-label>
                        <q-icon 
                            size="xs" 
                            :name="'mdi-' + (
                                (workItem.notes ?? '').length 
                                    ? 'note-text-outline' 
                                    : 'note-outline'
                            )" 
                        />
                    </q-item-label>
                </q-item-section>
                <q-item-section avatar
                    :class="{
                        'text-grey-7': !$q.dark.isActive,
                        'text-grey-2': $q.dark.isActive,
                    }"
                >Notes</q-item-section>
                <q-item-section
                    style="padding-left: 65px;"
                >
                    <q-item-label 
                        :lines="1"
                        :class="{
                            'text-right ellipsis':1,
                            'text-bold cursor-pointer': (workItem.notes ?? '').length,
                            'text-grey-5': !(workItem.notes ?? '').length
                        }"
                        style="text-wrap: pretty;"
                    >
                        {{ workItem.notes ?? '*N/A*' }}

                        <q-menu 
                            v-if="(workItem.notes ?? '').length"
                            anchor="bottom right" self="top right"
                        >
                            <q-item class="q-pa-md">
                                <q-item-section>
                                    <q-item-label class="text-grey-5">
                                        <q-icon 
                                            size="xs" 
                                            name="mdi-note-text-outline"
                                        />
                                        {{ workItem.work_type?.name ?? '' }} (ID: {{ workItem.id }})
                                    </q-item-label>
                                    <q-item-label style="max-width: 300px;">
                                        {{ workItem.notes ?? '*No notes available*' }}
                                    </q-item-label>
                                </q-item-section>
                            </q-item>
                            <q-card v-if="false" style="width: 300px;">
                                <q-card-section class="q-pb-none text-grey-5 no-wrap">
                                    <div>
                                        <q-icon 
                                            size="xs" 
                                            name="mdi-note-text-outline"
                                        />
                                        {{ workItem.work_type?.name ?? '' }} (ID: {{ workItem.id }})
                                    </div>
                                </q-card-section>
                                <q-card-section class="q-pt-xs">
                                    <div class="text-caption">
                                        {{ workItem.notes ?? 'No notes available' }}
                                    </div>
                                </q-card-section>
                            </q-card>
                        </q-menu>
                    </q-item-label>
                </q-item-section>
            </q-item>
            
            <q-item>
                <q-item-section style="padding-right: 5px;" side>
                    <q-item-label>
                        <q-icon size="xs" name="mdi-clock" />
                    </q-item-label>
                </q-item-section>
                <q-item-section 
                    :class="{
                        'text-grey-7': !$q.dark.isActive,
                        'text-grey-2': $q.dark.isActive,
                    }"
                >Created</q-item-section>
                <q-item-section side>{{ format(workItem.created_at) }}</q-item-section>
            </q-item>
            <q-item v-if="workItem.updated_at && workItem.updated_at != workItem.created_at">
                <q-item-section style="padding-right: 5px;" side>
                    <q-item-label>
                        <q-icon size="xs" name="mdi-clock-end" />
                    </q-item-label>
                </q-item-section>
                <q-item-section 
                    :class="{
                        'text-grey-7': !$q.dark.isActive,
                        'text-grey-2': $q.dark.isActive,
                    }"
                >Last Modified</q-item-section>
                <q-item-section side>{{ format(workItem.updated_at) }}</q-item-section>
            </q-item>
        </q-list>

        <SwapDialog
            v-model="swap.open"
            v-model:work-type-id="workItem.work_type_id"
            v-model:sub-customer-id="workItem.sub_customer_id"
            :customer="customer"
            @customer-change="handleCustomerChange"
            @work-type-change="handleWorkTypeChange"
        />

        <WorkerSplitDialog 
            v-model="workerSplit"
            v-model:pay="workItem.pay"
            @update:pay="handlePayChange"
            :customer-id="customer.id"
            :work-item-id="workItem.id"
            :work-start-at="workStartAt"
        >
            <template #title>
                Worker Split
                <q-item-label :class="{'text-grey-4': $q.dark.isActive}" caption>
                    {{ workItem.work_type?.name ?? '' }} (ID: {{ workItem.id }}) {{ 
                        $q.screen.lt.md ? '' : (' - ' + (workItem?.sub_customer?.contact?.org_name ?? workItem.customer?.contact?.org_name ?? 'Unknown')) 
                    }}
                </q-item-label>
            </template>
        </WorkerSplitDialog>
    </q-expansion-item>
</template>

<script>
import { format } from 'date-fns';

import Avatar from '@/Components/Avatar.vue';
import ContactViewModel from '@/Components/Models/Contact/ViewModel.vue';

import SwapDialog from '@/Components/Dialogs/WorkOrderDialog/WorkItemDetailedList/SwapDialog.vue';
import WorkerSplitDialog from '@/Components/Dialogs/WorkOrderDialog/WorkItemDetailedList/WorkerSplitDialog.vue';

export default {
    name: 'WorkItemView',
    emits: ['update:modelValue', 'delete'],
    components: {
        Avatar,
        SwapDialog,
        ContactViewModel,
        WorkerSplitDialog,
    },
    props: {
        customer: {
            type: Object,
            required: true
        },
        enableCrud: {
            type: Boolean,
            default: false
        },
        locked: {
            type: Boolean,
            default: false
        },
        modelValue: {
            type: Object,
            required: true
        },
        workStartAt: {
            type: String,
            required: true
        },
        error: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            workItem: this.modelValue,
            editingWorkers: {},
            swap: {
                open: false,
                customer: false,
                workType: false,
            },
            workerSplit: false,
            watchers: [],
            selected: {
                subCustomer: null,
                workType: null,
            }
        }
    },
    methods: {
        handlePayChange(pay) {
            this.workItem.pay = pay;
            this.$emit('update:modelValue', this.workItem);
        },
        handleCustomerChange(subCustomer) {
            this.workItem.sub_customer = subCustomer;
            this.workItem.sub_customer_id = subCustomer?.id ?? null;
        },
        handleWorkTypeChange(workType) {
            this.workItem.work_type = workType;
            this.workItem.work_type_id = workType?.id ?? null;
            this.$emit('update:modelValue', this.workItem);
        },
        cancelEdit(workItemId) {
            delete this.editingWorkers[workItemId];
        },
        isEditingLumpers(workItemId) {
            return this.editingWorkers[workItemId] ?? false;
        },
        processDarkRadial(color) {
            if (!color) {
                color = this.$q.dark.isActive ? 'black' : 'white';
                
                return this.isDarkRadial(color, 'white', 'black', 175);
            }
            // Check if we have a hex color
            if (color.match(/^#?[0-9A-F]{6}$/i)) {
                return this.isDarkRadial(color, 'white', 'black', 175);
            }
            // Assume it's a variable
            return this.isDarkRadial(this.getCssVar(color), 'white', 'black', 175);
        }
    },
    watch: {
        modelValue: {
            handler(to) {
                this.workItem = {...to};
            },
            deep: true
        },
    },
    computed: {
        editable() {
            return (this.enableCrud && !this.locked);
        },
    },
    beforeDestroy() {
        this.watchers.forEach(watcher => watcher());
    }
}
</script>